import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useCreateServicesMutation, useUpdateServicesMutation } from "../../../../../../../API/authApi";
import { useToastify } from "../../../../../../../Hooks/hooks";
import { IMyServicesForm } from "../../../../../../../Interfaces/formValue-interfaces";
import { IMyServicesRequestData } from "../../../../../../../Interfaces/request-interfaces";
import { IDineInConfig, IDineInUnit, IServices } from "../../../../../../../Interfaces/responseTypes";
import { IMyServicesProps } from "./myServices";
import useLanguagesHooks from "../../../../../../../Components/Languages/setLanguage-hooks";
import { toDineInUnitPayload, isActiveDineInUnit } from "./DineInUnits/dineInHelpers";

const DEFAULT_DINE_IN: IDineInConfig = {
    is_active: false,
    units: [],
};

const normalizeDineIn = (dineIn?: IDineInConfig): IDineInConfig => ({
    is_active: dineIn?.is_active ?? false,
    units: (dineIn?.units ?? []).map((unit) => ({
        ...unit,
        qr_data: unit.qr_data ?? null,
    })),
});

const areDineInUnitsEqual = (a: IDineInUnit[], b: IDineInUnit[]): boolean => {
    if (a.length !== b.length) return false;

    return a.every((unit, index) => {
        const other = b[index];
        return (
            unit.type === other.type &&
            unit.quantity === other.quantity &&
            (unit.extra_cost ?? null) === (other.extra_cost ?? null)
        );
    });
};

const areDineInEqual = (a?: IDineInConfig, b?: IDineInConfig): boolean => {
    const normalizedA = normalizeDineIn(a);
    const normalizedB = normalizeDineIn(b);

    return (
        normalizedA.is_active === normalizedB.is_active &&
        areDineInUnitsEqual(normalizedA.units, normalizedB.units)
    );
};

const buildFormValues = (services: IServices | null): IMyServicesForm => {
    if (!services) {
        return {
            delivery: { is_active: false, min_cost: null },
            pre_order: false,
            reserve: false,
            dine_in: DEFAULT_DINE_IN,
        };
    }

    return {
        ...services,
        dine_in: normalizeDineIn(services.dine_in),
    };
};

const useMyServicesHook = (props: IMyServicesProps) => {
    const { data, onUpdate } = props;
    const { t } = useLanguagesHooks();
    const [create, { isLoading: createLoading }] = useCreateServicesMutation();
    const [update, { isLoading: updateLoadng }] = useUpdateServicesMutation();
    const { showToastSuccess, showToastError } = useToastify();
    const isSubmittingRef = useRef(false);
    const lastSyncedServicesRef = useRef<string>('');

    const {
        register,
        control,
        setValue,
        reset,
        formState: { errors },
        watch,
    } = useForm<IMyServicesForm>({
        mode: 'all',
        defaultValues: buildFormValues(data?.services ?? null),
    });

    useEffect(() => {
        const serialized = JSON.stringify(data?.services ?? null);
        if (serialized !== lastSyncedServicesRef.current) {
            lastSyncedServicesRef.current = serialized;
            reset(buildFormValues(data?.services ?? null));
        }
    }, [data?.services, reset]);

    const deliveryActive = watch('delivery.is_active');
    const minCost = watch('delivery.min_cost');
    const preOrder = watch('pre_order');
    const reserve = watch('reserve');
    const dineInActive = watch('dine_in.is_active');
    const dineInUnits = watch('dine_in.units') ?? [];
    
    const minCostStr = minCost?.toString() ?? '';
    const isMinCostValid = minCostStr.trim() !== '' && !/^0\d+/.test(minCostStr) && Number(minCostStr) >= 1;
    const isDeliveryValid = !deliveryActive || (deliveryActive && isMinCostValid);
    const isDineInValid = !dineInActive || dineInUnits.length > 0;

    const currentValues: IServices = {
        delivery: {
            is_active: deliveryActive,
            min_cost: minCost,
        },
        pre_order: preOrder,
        reserve: reserve,
        dine_in: {
            is_active: dineInActive,
            units: dineInUnits,
        },
    };

    const areServicesEqual = useCallback((a: IServices, b: IServices): boolean => {
        return (
            a.delivery.is_active === b.delivery.is_active &&
            Number(a.delivery.min_cost) === Number(b.delivery.min_cost) &&
            a.pre_order === b.pre_order &&
            a.reserve === b.reserve &&
            areDineInEqual(a.dine_in, b.dine_in)
        );
    }, []);

    const isButtonActive = useMemo(() => {
        if (!isDineInValid) {
            return false;
        }

        const servicesExist = data?.services !== null;

        if (!servicesExist) {
            return (
                (deliveryActive && isMinCostValid) ||
                preOrder ||
                reserve ||
                (dineInActive && dineInUnits.some(isActiveDineInUnit))
            );
        }

        const serverValues: IServices = {
            ...data.services!,
            dine_in: normalizeDineIn(data.services!.dine_in),
        };

        const hasChanges = !areServicesEqual(serverValues, currentValues);
        return hasChanges && isDeliveryValid;

    }, [deliveryActive, minCost, preOrder, reserve, dineInActive, dineInUnits, data?.services, isMinCostValid, isDeliveryValid, isDineInValid, currentValues, areServicesEqual]);

    const onDineInUnitsChange = (units: IDineInUnit[]) => {
        setValue('dine_in.units', units, { shouldDirty: true });
    };

    const onSave = async () => {
        if (isSubmittingRef.current || createLoading || updateLoadng) {
            return;
        }
        if (!isDineInValid) {
            return;
        }
        isSubmittingRef.current = true;

        const normalizedMinCost =
            minCost === null || minCost === undefined || minCost === ("" as any)
                ? null
                : Number(minCost);

        const payload: IMyServicesRequestData = {
            id: data.id,
            body: {
                delivery: {
                    is_active: deliveryActive,
                    min_cost: normalizedMinCost,
                },
                pre_order: preOrder,
                reserve: reserve,
                dine_in: {
                    is_active: dineInActive,
                    units: dineInActive ? dineInUnits.map(toDineInUnitPayload) : [],
                },
            },
        };
        try {
            if (data?.services === null) {
                await create(payload).unwrap();
            } else {
                await update(payload).unwrap();
            }
            onUpdate();
            showToastSuccess(t('Toast.Success.Common'));
        } catch {
            showToastError(t('Error.Common'));
        } finally {
            isSubmittingRef.current = false;
        }
    };

    return {
        onSave,
        register,
        errors,
        control,
        updateLoadng,
        createLoading,
        deliveryActive,
        dineInActive,
        dineInUnits,
        onDineInUnitsChange,
        isButtonActive,
    };
};

export default useMyServicesHook;
